// Service Custom : personnalisation, performance, réparation et vente de
// manettes et consoles.
//
// NOTE INTERNE (invisible pour les visiteurs) : grille de prix provisoire,
// calée sur le marché français. À confirmer avec vos coûts réels de pièces et
// votre temps d'atelier avant l'ouverture des commandes.

export const CUSTOM = {
  eyebrow: "Custom",
  title: "Manettes et consoles, réglées pour vous.",
  lead: "Personnalisation, performance, entretien et réparation. Sur console comme sur manette, PlayStation, Xbox et Nintendo. Chaque pièce est un cas particulier : tout se chiffre sur devis.",
  notice: "Aucun prix n'est affiché sur cette page, et ce n'est pas un oubli : deux manettes identiques ne demandent pas le même travail selon la matière, la taille et la peinture choisie. Décrivez votre pièce, vous recevez un devis chiffré et ferme.",
};

export const PLATFORMS = {
  manettes: [
    "Xbox Series X|S et Xbox One",
    "PlayStation 5 (DualSense et DualSense Edge)",
    "PlayStation 4 (DualShock 4)",
    "Nintendo Switch Pro Controller",
    "Manettes pro tierces (Scuf, Razer, Victrix)",
  ],
  consoles: [
    "PlayStation 5 et PS5 Slim",
    "PlayStation 4, Slim et Pro",
    "Xbox Series X et Series S",
    "Xbox One, S et X",
    "Nintendo Switch et Switch OLED",
  ],
};

export const SERVICES = [
  {
    key: "console",
    icon: "Gamepad2",
    title: "Console",
    lead: "Personnalisation complète et entretien. Une console qui chauffe moins dure plus longtemps.",
    items: [
      { label: "Skin adhésif", note: "Pose professionnelle, sans bulle, retirable sans trace." },
      { label: "Coques de remplacement", note: "Coques latérales de couleur, montées et ajustées." },
      { label: "Peinture personnalisée", note: "Coques démontées, peintes et vernies. Motif de votre choix.", highlight: true },
      { label: "Hydro-dipping console", note: "Motif complexe appliqué par immersion sur les coques." },
      { label: "Éclairage LED interne", note: "Rétroéclairage diffus, couleur réglable, alimenté en interne." },
      { label: "Gravure du logo ou du pseudo", note: "Gravure laser sur la coque ou le socle." },
      { label: "Entretien et pâte thermique", note: "Dépoussiérage complet et remplacement de la pâte. Fait tomber le bruit du ventilateur." },
      { label: "Réparation ventilation", note: "Remplacement du ventilateur et nettoyage du dissipateur." },
      { label: "Remplacement du lecteur de disque", note: "Sur les modèles équipés, pièce comprise." },
      { label: "Pack console complet", note: "Peinture personnalisée, LED internes, gravure et entretien.", highlight: true },
    ],
  },
  {
    key: "performance",
    icon: "Target",
    title: "Performance",
    lead: "Ce qui change la façon dont vous jouez, pas l'allure de la manette.",
    items: [
      { label: "Palettes arrière — 2", note: "Deux actions déportées sous les doigts majeurs." },
      { label: "Palettes arrière — 4", note: "Pour libérer complètement le pouce droit." },
      { label: "Gâchettes à course courte", note: "Butée mécanique : déclenchement quasi instantané. Indispensable en FPS." },
      { label: "Sticks à effet Hall (anti-drift)", note: "Capteurs magnétiques sans contact : plus d'usure, plus de dérive." },
      { label: "Sticks interchangeables", note: "Trois hauteurs, deux formes de capuchon." },
      { label: "Pack performance complet", note: "Palettes 4, gâchettes courtes, sticks Hall et sticks interchangeables.", highlight: true },
    ],
  },
  {
    key: "esthetique",
    icon: "Sparkles",
    title: "Esthétique",
    lead: "Une manette qu'on reconnaît à distance sur une table de tournoi.",
    items: [
      { label: "Coque de couleur", note: "Teintes unies, mates ou brillantes." },
      { label: "Peinture personnalisée", note: "Motif de votre choix, peinture puis vernis de protection." },
      { label: "Hydro-dipping", note: "Motif complexe appliqué par immersion, rendu continu sur toute la coque." },
      { label: "Éclairage LED", note: "Anneaux de sticks, boutons ou logo. Couleur réglable." },
      { label: "Gravure", note: "Pseudo, logo d'équipe ou numéro, gravés au laser." },
    ],
  },
  {
    key: "reparation",
    icon: "LifeBuoy",
    title: "Réparation",
    lead: "Une manette qui dérive se remplace rarement : elle se répare.",
    items: [
      { label: "Diagnostic", note: "Déduit du montant si vous faites réaliser la réparation." },
      { label: "Dérive d'un stick", note: "Remplacement du module. Passage à l'effet Hall possible en supplément." },
      { label: "Dérive des deux sticks" },
      { label: "Gâchette ou bouton", note: "Remplacement du mécanisme." },
      { label: "Nettoyage complet", note: "Démontage, dépoussiérage, remplacement des membranes." },
    ],
  },
];

// Manettes déjà modifiées, prêtes à jouer. Le prix inclut la manette.
export const READY = [
  {
    name: "Custom Essentiel",
    text: "Une manette neuve avec l'essentiel : deux palettes et une coque de couleur.",
    includes: ["Manette neuve", "2 palettes arrière", "Coque de couleur au choix", "Garantie 6 mois sur les interventions"],
  },
  {
    name: "Custom Pro", highlight: true,
    text: "La configuration que choisissent les joueurs classés : quatre palettes, gâchettes courtes, sticks anti-dérive.",
    includes: ["Manette neuve", "4 palettes arrière", "Gâchettes à course courte", "Sticks à effet Hall", "Coque de couleur au choix", "Garantie 6 mois"],
  },
  {
    name: "Custom Signature",
    text: "La configuration Pro, avec une peinture personnalisée et votre pseudo gravé.",
    includes: ["Tout le pack Pro", "Peinture personnalisée", "Gravure de votre pseudo", "Étui rigide", "Garantie 6 mois"],
  },
];

// Consoles déjà personnalisées, prêtes à brancher. Le prix inclut la console.
export const READY_CONSOLES = [
  {
    name: "Console Custom Signature",
    text: "Une console neuve, coques peintes selon votre motif et pseudo gravé.",
    includes: ["Console neuve", "Peinture personnalisée", "Gravure de votre pseudo", "Pâte thermique haut de gamme posée", "Garantie 6 mois sur les interventions"],
  },
  {
    name: "Reprise et personnalisation",
    text: "Vous envoyez votre console, elle revient personnalisée et entretenue.",
    includes: ["Peinture ou hydro-dipping au choix", "Éclairage LED interne", "Entretien complet et pâte thermique", "Gravure comprise", "Garantie 6 mois"],
  },
];

// Les cinq éléments qui font varier un devis. Les nommer évite la question
// « pourquoi ce prix » et le sentiment d'arbitraire.
export const QUOTE_FACTORS = [
  { title: "L'objet", text: "Une manette, une console, un casque ou un volant ne se démontent pas de la même façon. Certaines pièces s'ouvrent en dix minutes, d'autres demandent un désassemblage complet." },
  { title: "La matière", text: "Plastique brut, plastique soft-touch, métal, surface déjà peinte ou vernie : chacune demande une préparation différente. Une surface soft-touch doit être poncée entièrement avant toute peinture." },
  { title: "La taille", text: "La surface à traiter détermine la quantité de matière, le temps de ponçage, de peinture et de séchage. Une console représente plusieurs fois la surface d'une manette." },
  { title: "Le type de personnalisation", text: "Une teinte unie, un dégradé, un motif multicouche au pochoir ou une illustration à main levée ne demandent ni le même temps ni la même technique." },
  { title: "Le type de peinture", text: "Peinture acrylique, candy, nacrée, chrome, thermosensible ou phosphorescente : les écarts de prix entre produits sont importants, et certaines exigent plusieurs couches de vernis." },
];

export const PROCESS = [
  { title: "Vous décrivez le besoin", text: "Votre plateforme, votre jeu principal, ce qui vous gêne aujourd'hui. Une manette de joueur FPS ne se règle pas comme une manette de joueur de foot." },
  { title: "On valide ensemble", text: "Devis détaillé, délai annoncé. Rien ne démarre avant votre accord." },
  { title: "Passage en atelier", text: "Cinq à dix jours ouvrés selon l'intervention. Une option express en 48 heures existe : elle figure au devis." },
  { title: "Retour et réglages", text: "Manette testée avant envoi. Six mois de garantie sur toute intervention." },
];

export const CUSTOM_FAQ = [
  { q: "Pourquoi aucun prix n'est affiché ?", a: "Parce qu'un prix fixe serait faux dans la plupart des cas. Une peinture unie sur une manette plastique et un dégradé candy trois couches sur une console soft-touch n'ont rien à voir en temps de travail ni en matière. Afficher un tarif unique reviendrait soit à surfacturer les demandes simples, soit à perdre de l'argent sur les complexes." },
  { q: "Le devis peut-il changer en cours de route ?", a: "Non. Le devis validé est ferme. Si nous découvrons un problème en ouvrant la pièce — une casse antérieure, une réparation mal faite — nous vous appelons avant de continuer. Rien n'est facturé sans votre accord." },
  { q: "Ma garantie constructeur saute-t-elle ?", a: "Oui, dès l'ouverture de la coque — c'est vrai chez tous les ateliers de personnalisation. Nous la remplaçons par six mois de garantie sur les pièces posées et le travail réalisé." },
  { q: "Est-ce autorisé en compétition ?", a: "Les palettes et les gâchettes à course courte sont admises dans la plupart des règlements, mais chaque tournoi a les siens. Dites-nous lesquels vous visez : nous vérifions avant de modifier quoi que ce soit." },
  { q: "Vous fournissez la manette ou j'envoie la mienne ?", a: "Les deux. Envoyez la vôtre pour une modification ou une réparation, ou commandez une manette déjà configurée dans la section prête à jouer." },
  { q: "Combien de temps sans ma manette ?", a: "Cinq à dix jours ouvrés d'atelier, hors transport. Une option express ramène ce délai à 48 heures ; son supplément figure au devis." },
  { q: "L'effet Hall règle-t-il vraiment la dérive ?", a: "Oui. La dérive vient de l'usure de pistes résistives ; les capteurs à effet Hall sont magnétiques et sans contact, donc sans usure. C'est la seule modification qui supprime le problème au lieu de le repousser." },
  { q: "Démonter ma console, c'est risqué ?", a: "Une PS5 ou une Xbox se démonte sans outil spécial jusqu'aux coques. Pour la peinture, seules les coques extérieures partent en atelier — l'électronique ne bouge pas. Pour l'entretien, le démontage va plus loin : c'est là que les six mois de garantie comptent." },
  { q: "L'entretien change-t-il vraiment quelque chose ?", a: "Sur une console de plus de deux ans, oui. La pâte thermique sèche, le dissipateur s'encrasse, le ventilateur compense en tournant plus vite. Le remplacement fait souvent tomber le bruit de plusieurs décibels et repousse la limitation thermique." },
  { q: "Et si le résultat ne me convient pas ?", a: "Pour une intervention esthétique, une reprise est offerte si le rendu ne correspond pas au devis validé. Pour la performance, un réglage de tension ou de course est inclus au retour." },
];

export const WARRANTY_MONTHS = 6;
